import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import ThemeContext from '../../ThemeContext';
import CaseManagement from '../pages/CaseManagement';
import UserManagement from '../pages/UserManagement';
import Account from '../pages/Account';
import ClientConfig from '../../../config/Clientconfig';
import { URL_SEGMENTS } from '../../../config/Routesconfig';

// ─────────────────────────────────────────────────────────────────────────────
// Read user once at module level — avoids re-parsing localStorage on each render
// ─────────────────────────────────────────────────────────────────────────────
const _storedUser   = JSON.parse(localStorage.getItem('user')) || {};
const _initialRole  = _storedUser?.role || '';
const _isClientUser = _initialRole.toLowerCase() === 'client_user';

const ClientDashboard = () => {
  useTerminology();
  const { theme }    = useContext(ThemeContext);
  const navigate     = useNavigate();
  const location     = useLocation();
  const { username } = useParams();

  const [activeTab,    setActiveTab]    = useState('cases');
  const [currentUser,  setCurrentUser]  = useState(_storedUser);
  const [error,        setError]        = useState('');

  // ── Typography ──────────────────────────────────────────────────────────
  const fontFamily   = theme.typography?.fontFamily   || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  // ── Route → active tab sync ─────────────────────────────────────────────
  useEffect(() => {
    const pathSegments    = location.pathname.split('/').filter(Boolean);
    const dashboardIndex  = pathSegments.indexOf('dashboard');
    const subPath         = pathSegments.slice(dashboardIndex + 1);

    let newTab = 'cases';
    if (subPath[0] === URL_SEGMENTS.users ||
        subPath[0] === URL_SEGMENTS.addUser ||
        subPath[0] === URL_SEGMENTS.editUser?.split('/')[0]) {
      newTab = 'users';
    } else if (subPath[0] === URL_SEGMENTS.account) {
      newTab = 'account';
    }

    setActiveTab(newTab);

    if (subPath.length === 0) {
      navigate(`/${username}/dashboard/${URL_SEGMENTS.cases}`, { replace: true });
    }
  }, [navigate, location, username]);

  // ── Tab click ──────────────────────────────────────────────────────────
  const handleTabClick = useCallback((tab) => {
    setActiveTab(tab);
    const segmentMap = {
      cases  : URL_SEGMENTS.cases,
      users  : URL_SEGMENTS.users,
      account: URL_SEGMENTS.account,
    };
    navigate(`/${username}/dashboard/${segmentMap[tab]}`);
  }, [navigate, username]);

  // ── Content router ─────────────────────────────────────────────────────
  const renderContent = () => {
    const path = location.pathname;
    const isSubPage =
      path.includes(`/${URL_SEGMENTS.viewCase}/`)  ||
      path.includes(`/${URL_SEGMENTS.addUser}`)    ||
      path.includes(`/${URL_SEGMENTS.editUser}/`)  ||
      path.includes(`/${URL_SEGMENTS.addCase}`)    ||
      path.includes(`/${URL_SEGMENTS.editCase}`)   ||
      path.includes(`/${URL_SEGMENTS.sampleCase}`);

    if (isSubPage) return <Outlet />;

    switch (activeTab) {
      case 'cases':   return <CaseManagement />;
      case 'users':   return <UserManagement />;
      case 'account': return (
        <Account
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
          setError={setError}
        />
      );
      default: return <div>No content available for this tab.</div>;
    }
  };

  // ── Shared tab button style factory ───────────────────────────────────
  const tabStyle = (tab) => ({
    flex           : '1 1 0',
    minWidth       : '100px',
    padding        : '0.4rem',
    fontSize       : `${bodyFontSize}px`,
    fontWeight     : 'bold',
    textAlign      : 'center',
    borderRadius   : '5px',
    border         : 'none',
    background     : activeTab === tab ? theme.gradients.primary : theme.gradients.secondary,
    color          : theme.colors.textWhite,
    cursor         : 'pointer',
    transition     : 'opacity 0.2s ease',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    gap            : '0.5rem',
    fontFamily,
  });

  const iconStyle = {
    fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily,
        fontSize    : `${bodyFontSize}px`,
        width       : '100%',
        boxSizing   : 'border-box',
        textAlign   : 'initial',
      }}
    >
      {/* ── TAB BAR ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display       : 'flex',
          gap           : '0.5rem',
          marginBottom  : '1rem',
          justifyContent: 'center',
          flexWrap      : 'wrap',
        }}
      >
        {/* Cases tab */}
        <button
          style={tabStyle('cases')}
          onClick={() => handleTabClick('cases')}
          onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseOut={(e)  => (e.currentTarget.style.opacity = '1')}
        >
          <i className={`bi bi-${ClientConfig.nav.cases.icon}`} style={iconStyle} />
          {ClientConfig.nav.cases.label}
        </button>

        {/* Users tab — hidden for client_user role */}
        {!_isClientUser && (
          <button
            style={tabStyle('users')}
            onClick={() => handleTabClick('users')}
            onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseOut={(e)  => (e.currentTarget.style.opacity = '1')}
          >
            <i className={`bi bi-${ClientConfig.nav.users.icon}`} style={iconStyle} />
            {ClientConfig.nav.users.label}
          </button>
        )}

        {/* Account tab */}
        <button
          style={tabStyle('account')}
          onClick={() => handleTabClick('account')}
          onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
          onMouseOut={(e)  => (e.currentTarget.style.opacity = '1')}
        >
          <i className={`bi bi-${ClientConfig.nav.account.icon}`} style={iconStyle} />
          {ClientConfig.nav.account.label}
        </button>
      </div>

      {/* ── ERROR BANNER (from Account page) ─────────────────────────────── */}
      {error && (
        <div
          style={{
            padding        : '0.75rem 1.25rem',
            marginBottom   : '1rem',
            border         : `1px solid ${theme.colors.errorBorder}`,
            borderRadius   : '0.25rem',
            color          : theme.colors.errorText,
            backgroundColor: theme.colors.errorBg,
            textAlign      : 'center',
            width          : '100%',
            boxSizing      : 'border-box',
            fontFamily,
            fontSize       : `${bodyFontSize}px`,
          }}
        >
          {error}
        </div>
      )}

      {/* ── PAGE CONTENT ──────────────────────────────────────────────────── */}
      <div style={{ width: '100%', boxSizing: 'border-box' }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default ClientDashboard;